import type { RouteLocationRaw } from "vue-router";

type RouterAnimation = unknown;

type HookList = Array<() => void> | undefined;

function readHooks(
  instance: object,
  key: string,
): Array<() => void> | undefined {
  const direct = (instance as Record<string, unknown>)[key];
  if (Array.isArray(direct)) return direct as Array<() => void>;

  const exposed = (instance as { $?: { exposed?: Record<string, unknown> } })
    .$?.exposed?.[key];
  if (Array.isArray(exposed)) return exposed as Array<() => void>;

  // script setup stores composition hooks on the internal proxy
  const proxy = (instance as { $?: { proxy?: Record<string, unknown> } }).$
    ?.proxy?.[key];
  if (Array.isArray(proxy)) return proxy as Array<() => void>;

  return undefined;
}

function runHooks(hooks: HookList) {
  if (!hooks?.length) return;
  for (const hook of hooks) hook();
}

/**
 * Ionic's useIonRouter() injects `navManager` from @ionic/vue-router.
 * We use Nuxt's vue-router for marketing SSR, so provide a compatible shim.
 *
 * Also fires onIonView* hooks on mount/unmount because NuxtPage does not use
 * IonRouterOutlet (which normally invokes those hooks).
 */
export default defineNuxtPlugin({
  name: "ionic-nav-manager-shim",
  enforce: "pre",
  setup(nuxtApp) {
    const router = useRouter();

    const resolvePath = (location: RouteLocationRaw) => {
      if (typeof location === "string") return location;
      return router.resolve(location).fullPath;
    };

    const handleNavigate = (
      location: RouteLocationRaw,
      routerAction: "push" | "replace" | "pop" = "push",
      _routerDirection?: string,
      _routerAnimation?: RouterAnimation,
    ) => {
      const path = resolvePath(location);
      if (routerAction === "replace") {
        return router.replace(path);
      }
      return router.push(path);
    };

    const navManager = {
      canGoBack: () =>
        import.meta.client ? window.history.length > 1 : false,
      goBack: (_routerAnimation?: RouterAnimation) => {
        router.back();
      },
      goForward: (_routerAnimation?: RouterAnimation) => {
        router.forward();
      },
      handleNavigate,
      handleNavigateBack: (
        defaultHref?: string,
        _routerAnimation?: RouterAnimation,
      ) => {
        if (import.meta.client && window.history.length > 1) {
          router.back();
          return;
        }
        if (defaultHref) {
          void router.replace(defaultHref);
        }
      },
    };

    nuxtApp.vueApp.provide("navManager", navManager);

    nuxtApp.vueApp.mixin({
      mounted() {
        runHooks(readHooks(this, "onIonViewWillEnter"));
        runHooks(readHooks(this, "onIonViewDidEnter"));
      },
      beforeUnmount() {
        runHooks(readHooks(this, "onIonViewWillLeave"));
        runHooks(readHooks(this, "onIonViewDidLeave"));
      },
    });
  },
});
